

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTravelContactModal from 'components/NewTravelContactModal';

const TravelContacts = () => {
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('travel_contacts');

    useEffect(() => {
        fetchContacts();
    }, []);

    const fetchContacts = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/travel-contacts/');
            setContacts(response.data);
        } catch (error) {
            console.error('Error fetching travel contacts:', error);
            setSnackbar({ open: true, message: 'Error loading travel contacts', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveContact = async (payload) => {
        try {
            await axiosInstance.post('/travel-contacts/', payload);
            setSnackbar({ open: true, message: 'Travel Contact created successfully', severity: 'success' });
            fetchContacts();
        } catch (error) {
            console.error('Error saving travel contact:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/travel-contact/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Contact Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Contact",
            accessor: "contact_id",
            width: "12%",
            Cell: ({ row }) => {
                const contact = row.original.contact;
                return contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || row.original.contact_id : '-'
            }
        },
        {
            Header: "Account",
            accessor: "account_id",
            width: "12%",
            Cell: ({ row }) => row.original.account?.name || row.original.account_id || '-'
        },
        {
            Header: "Email",
            accessor: "email",
            width: "15%",
            Cell: ({ value, row }) => value || row.original.contact?.email || '-'
        },
        {
            Header: "Phone",
            accessor: "phone",
            width: "12%",
            Cell: ({ value, row }) => value || row.original.contact?.phone || row.original.contact?.mobile_number || '-'
        },
        {
            Header: "Record Type",
            accessor: "record_type",
            width: "12%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Passenger'}
                    size="small"
                    color={
                        value === 'Passenger' ? 'primary' :
                        value === 'Guide' ? 'success' :
                        value === 'Merchant' ? 'warning' : 'default'
                    }
                />
            )
        },
        {
            Header: "Primary Contact",
            accessor: "primary_contact",
            width: "10%",
            Cell: ({ value }) => (
                <Chip
                    label={value ? 'Yes' : 'No'}
                    size="small"
                    color={value ? 'success' : 'default'}
                />
            )
        },
        {
            Header: "Passport Number",
            accessor: "passport_number",
            width: "12%",
            Cell: ({ value }) => value || '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: contacts,
    }), [contacts]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Contacts</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Travel Contact
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : contacts.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Contact" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTravelContactModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveContact}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelContacts;

